import { useCallback, useState } from "react";

import Api from "../../services/Api";
import { dayjs } from "../../services/dayjs";
import useAuth from "../../routes/login/AuthContext";
import { getDefaultDate } from "../../helpers/getDefaultDate";
import { useModalContext } from "../../contexts/ModalContext/context";
import { useSnackbar } from "../../hooks/useSnackbar";
import {
  useNetworkContext,
  useSelectedNetworkContext,
} from "../../contexts/NetworkContext/context";
import { usePrevious } from "../../helpers/usePrevious";

import type { MatchDate } from "./types";

interface IRequestData {
  startDate?: string;
  endDate?: string;
  activationStartDate?: string;
  activationEndDate?: string;
  matchingRoundStatus?: string;
  clear?: boolean;
  notify?: boolean;
}

interface PutRequestData {
  matchingStartDate?: string;
  matchingEndDate?: string;
  activationStartDate?: string;
  activationEndDate?: string;
  clear?: boolean;
  notify?: boolean;
}


export const useDatesHandler = (props: MatchDate) => {
  const { getAuthToken } = useAuth();
  const [isEndLoading, setIsEndLoading] = useState(false);
  const { setIsOpen } = useModalContext();
  const { updateSubNetworkValues } = useNetworkContext();
  const { selectedNetworkId } = useSelectedNetworkContext();
  const { showSuccessSnackbar, showErrorSnackbar } = useSnackbar();
  const [matchingEndDate, setMatchingEndDate] = useState<dayjs.Dayjs | null>(
    getDefaultDate(props.matchingEndDate)
  );
  const [matchingStartDate, setMatchingStartDate] = useState<dayjs.Dayjs | null>(
    getDefaultDate(props.matchingStartDate)
  );
  const [activationEndDate, setActivationEndDate] = useState<dayjs.Dayjs | null>(
      getDefaultDate(props.activationEndDate)
  );
  const [activationStartDate, setActivationStartDate] = useState<dayjs.Dayjs | null>(
      getDefaultDate(props.activationStartDate)
  );
  const [areActivationDateEnabled, setActivationDateEnabled] = useState(false);

  const prevEndData = usePrevious(matchingEndDate);
  const prevActivationEndDate = usePrevious(activationEndDate);

  const updateLocalNetworkData = useCallback(
    (data: IRequestData) => {
      if (data.clear) {
        updateSubNetworkValues(selectedNetworkId, props.l2Id, {
          matchingEndDate: undefined,
          matchingStartDate: undefined,
          activationStartDate: undefined,
          activationEndDate: undefined
        });
        return;
      } else if(data) {
        updateSubNetworkValues(selectedNetworkId, props.l2Id,
          {
            matchingEndDate: data.endDate,
            matchingStartDate: data.startDate,
            activationStartDate: data.activationStartDate,
            activationEndDate: data.activationEndDate,
            matchingRoundStatus: data.matchingRoundStatus
          });
        return;
      }
    },
    [updateSubNetworkValues, selectedNetworkId, props.l2Id]
  );

  const handleRequest = useCallback(
    async (data: PutRequestData, successMessage = "Saved Changes") => {
      setIsEndLoading(true);
      try {
        const result = await Api.requestDatesChange(
          selectedNetworkId,
          { [props.l2Id]: data },
          await getAuthToken()
        );
        if (result?.success) {
          updateLocalNetworkData(result.response.changes[0]);
          setIsEndLoading(false);
          showSuccessSnackbar(successMessage);
        } else {
          setIsEndLoading(false);
          showErrorSnackbar(result?.error.message);
        }
      } catch (error) {
        setIsEndLoading(false);
        showErrorSnackbar("An error occured. Please try again");
      }
    },
    [props.l2Id]
  );

  const handleDatesClear = async () => {
    handleRequest({ clear: true });
    setMatchingEndDate(null);
    setMatchingStartDate(null);
    setActivationStartDate(null);
    setActivationEndDate(null);
    setActivationDateEnabled(false);
  };

  const handleEndDateNotifyRequest = async (notify: boolean) => {
    setIsEndLoading(true);
    if(!areActivationDateEnabled) {
      handleRequest({
        matchingStartDate: matchingStartDate?.format("YYYY-MM-DD"),
        matchingEndDate: matchingEndDate?.format("YYYY-MM-DD"),
        activationStartDate: matchingStartDate?.format("YYYY-MM-DD"),
        activationEndDate: matchingEndDate?.format("YYYY-MM-DD"),
      }, notify ? "Email has been sent!" : "Changes saved.");
    } else {
      handleRequest({
        matchingStartDate: matchingStartDate?.format("YYYY-MM-DD"),
        matchingEndDate: matchingEndDate?.format("YYYY-MM-DD"),
        activationStartDate: activationStartDate?.format("YYYY-MM-DD"),
        activationEndDate: activationEndDate?.format("YYYY-MM-DD"),
      }, notify ? "Email has been sent!" : "Changes saved.");
    }
    setIsEndLoading(false);
    setIsOpen(false);
  };

  

  const onMatchingStartDateChange = (date: string | dayjs.Dayjs | null) => {
    date = date ? dayjs(date) : null;
    setMatchingStartDate(date);

    let checkActivationDates = areActivationDateEnabled && (!activationStartDate || !activationEndDate);

    if (!matchingEndDate || checkActivationDates) {
      return;
    }

    if(!areActivationDateEnabled) {
      handleRequest({
        matchingStartDate: date?.format("YYYY-MM-DD"),
        matchingEndDate: matchingEndDate?.format("YYYY-MM-DD"),
        activationStartDate: date?.format("YYYY-MM-DD"),
        activationEndDate: matchingEndDate?.format("YYYY-MM-DD"),
      });
    } else {
      handleRequest({
        matchingStartDate: date?.format("YYYY-MM-DD"),
        matchingEndDate: matchingEndDate?.format("YYYY-MM-DD"),
        activationStartDate: activationStartDate?.format("YYYY-MM-DD"),
        activationEndDate: activationEndDate?.format("YYYY-MM-DD"),
      });
    }
  };

  const onMatchingEndDateChange = (date: string | dayjs.Dayjs | null) => {
    date = date ? dayjs(date) : null;
    setMatchingEndDate(date);

    let checkActivationDates = areActivationDateEnabled && (!activationStartDate || !activationEndDate);

    if (!matchingStartDate || checkActivationDates) {
      return;
    }

    if (props.usersInNetwork > 0) {
      setIsOpen(true);
      return;
    }
    if(!areActivationDateEnabled) {
      handleRequest({
        matchingStartDate: matchingStartDate?.format("YYYY-MM-DD"),
        matchingEndDate: date?.format("YYYY-MM-DD"),
        activationStartDate: matchingStartDate?.format("YYYY-MM-DD"),
        activationEndDate: date?.format("YYYY-MM-DD"),
      });
    } else {
      handleRequest({
        matchingStartDate: matchingStartDate?.format("YYYY-MM-DD"),
        matchingEndDate: date?.format("YYYY-MM-DD"),
        activationStartDate: activationStartDate?.format("YYYY-MM-DD"),
        activationEndDate: activationEndDate?.format("YYYY-MM-DD"),
      });
    }

    
  };

  const onActivationStartDateChange = (date: string | dayjs.Dayjs | null) => {
    date = date ? dayjs(date) : null;
    setActivationStartDate(date);

    if (!matchingStartDate || !matchingEndDate || !activationEndDate) {
      return;
    }

    handleRequest({
      matchingStartDate: matchingStartDate?.format("YYYY-MM-DD"),
      matchingEndDate: matchingEndDate?.format("YYYY-MM-DD"),
      activationStartDate: date?.format("YYYY-MM-DD"),
      activationEndDate: activationEndDate?.format("YYYY-MM-DD"),
    });
  };
  const onActivationEndDateChange = (date: string | dayjs.Dayjs | null) => {
    date = date ? dayjs(date) : null;
    setActivationEndDate(date);

    if (!matchingStartDate || !matchingEndDate || !activationStartDate) {
      return;
    }

    handleRequest({
      matchingStartDate: matchingStartDate?.format("YYYY-MM-DD"),
      matchingEndDate: matchingEndDate?.format("YYYY-MM-DD"),
      activationStartDate: activationStartDate?.format("YYYY-MM-DD"),
      activationEndDate: date?.format("YYYY-MM-DD"),
    });
  };

  const onDeselectActivationDates = (currMatchingStartDate: string | dayjs.Dayjs | null, currMatchingEndDate: string | dayjs.Dayjs | null) => {
    currMatchingStartDate = currMatchingStartDate ? dayjs(currMatchingStartDate) : null;
    currMatchingEndDate = currMatchingEndDate? dayjs(currMatchingEndDate) : null;
    setActivationStartDate(currMatchingStartDate);
    setActivationEndDate(currMatchingEndDate);

    if (!matchingStartDate || !matchingEndDate || !activationEndDate) {
      return;
    }

    handleRequest({
      matchingStartDate: matchingStartDate?.format("YYYY-MM-DD"),
      matchingEndDate: matchingEndDate?.format("YYYY-MM-DD"),
      activationStartDate: currMatchingStartDate?.format("YYYY-MM-DD"),
      activationEndDate: currMatchingEndDate?.format("YYYY-MM-DD"),
    });
  };

  const handleModalNotificationModalClose = () => {
    setMatchingEndDate(prevEndData);
    setActivationEndDate(prevActivationEndDate);
  };

  const isPrevEndDateSelected =
    prevEndData && matchingEndDate ? dayjs(prevEndData).isAfter(matchingEndDate, "d") : false;

  return {
    areActivationDateEnabled,
    setActivationDateEnabled,
    matchingStartDate,
    matchingEndDate,
    activationStartDate,
    activationEndDate,
    isPrevEndDateSelected,
    isEndLoading,
    handleDatesClear,
    onMatchingStartDateChange,
    onMatchingEndDateChange,
    onActivationStartDateChange,
    onActivationEndDateChange,
    onDeselectActivationDates,
    handleEndDateNotifyRequest,
    handleModalNotificationModalClose,
  };
};
